import { DateTime } from 'luxon'
import type { QueryKey } from '@tanstack/react-query'
import type { OfflineEntity } from './db'
import { queryClient } from '../queryClient'
import { SYNC_ENTITIES } from './syncEntities'

type CachedPage = { data?: unknown[] }


const DRILL_BLAST_ROOTS: QueryKey[] = [['drilling'], ['drillBlast'], ['dataTable', 'drilling']]
const DRILL_BLAST_ID_FIELDS = new Set(['id', 'drillingId', 'drillingBlastId'])

export function patchCachedRowVersion(
  entity: OfflineEntity,
  entityId: number | undefined,
  rowVersion: string | undefined,
): void {
  if (!entityId || !rowVersion) return
  if (entity === 'drillingLine') {
    patchDrillingLineRowVersion(entityId, rowVersion)
    return
  }

  const idField = SYNC_ENTITIES[entity]?.idField
  if (!idField) return

  const matches = (row: unknown): boolean =>
    !!row && typeof row === 'object' && (row as Record<string, unknown>)[idField] === entityId

  for (const [key, cached] of queryClient.getQueriesData<CachedPage>({ queryKey: ['dataTable'] })) {
    const rows = cached?.data
    if (!Array.isArray(rows) || !rows.some(matches)) continue

    queryClient.setQueryData(key, {
      ...cached,
      data: rows.map(row => matches(row)
        ? Object.assign(Object.create(Object.getPrototypeOf(row)), row, { rowVersion })
        : row),
    })
  }
}

function patchDrillingLineRowVersion(lineId: number, rowVersion: string) {
  const isLine = (row: unknown) => (row as { id?: number } | null)?.id === lineId
  for (const [key, cached] of queryClient.getQueriesData<CachedPage>({ queryKey: ['dataTable', 'drilling', 'blastHoles'] })) {
    const rows = cached?.data
    if (!Array.isArray(rows) || !rows.some(isLine)) continue
    queryClient.setQueryData(key, { ...cached, data: rows.map(row => isLine(row) ? { ...(row as object), rowVersion } : row) })
  }
}

export function patchCachedEntityId(
  entity: OfflineEntity,
  tempId: number | undefined,
  serverId: number | undefined,
): void {
  if (!tempId || !serverId || tempId >= 0) return
  patchDrillBlastTempId(tempId, serverId)

  const idField = SYNC_ENTITIES[entity]?.idField
  if (!idField) return

  const matches = (row: unknown): boolean =>
    !!row && typeof row === 'object' && (row as Record<string, unknown>)[idField] === tempId

  for (const [key, cached] of queryClient.getQueriesData<CachedPage>({ queryKey: ['dataTable'] })) {
    const rows = cached?.data
    if (!Array.isArray(rows) || !rows.some(matches)) continue

    queryClient.setQueryData(key, {
      ...cached,
      data: rows.map(row => (matches(row) ? { ...(row as object), [idField]: serverId } : row)),
    })
  }
}

// Note: temp ids are negative and unique while queued, so a drill and blast id field holding one is the row just synced
// or a child naming it, and a query keyed by one (a pattern's lines) is copied to the real id's key.
function patchDrillBlastTempId(tempId: number, serverId: number) {
  const swap = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(swap)
    if (!value || typeof value !== 'object' || DateTime.isDateTime(value)) return value
    return Object.assign(Object.create(Object.getPrototypeOf(value)), Object.fromEntries(Object.entries(value).map(([field, v]) =>
      [field, DRILL_BLAST_ID_FIELDS.has(field) && v === tempId ? serverId : swap(v)])))
  }

  for (const root of DRILL_BLAST_ROOTS) {
    for (const [key, cached] of queryClient.getQueriesData({ queryKey: root })) {
      if (cached === undefined) continue
      const patched = swap(cached)
      const realKey: QueryKey = key.map(part => part === tempId ? serverId : part)
      if (realKey.some((part, i) => part !== key[i])) {
        if (queryClient.getQueryData(realKey) === undefined) queryClient.setQueryData(realKey, patched)
      } else if (JSON.stringify(patched) !== JSON.stringify(cached)) {
        queryClient.setQueryData(key, patched)
      }
    }
  }
}
